import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContentBlockType, ModuleItemType } from '@eduborg/shared';
import { api, type ContentBlockShape } from '@/lib/api';

export const courseDetailKey = (orgSlug: string, courseSlug: string) =>
  ['course', orgSlug, courseSlug] as const;

export const modulesKey = (courseId: string) => ['course', courseId, 'modules'] as const;
export const itemsKey = (moduleId: string) => ['module', moduleId, 'items'] as const;
export const blocksKey = (itemId: string) => ['item', itemId, 'blocks'] as const;

export function useCourseDetailQuery(token: string, orgSlug: string, courseSlug: string) {
  return useQuery({
    queryKey: courseDetailKey(orgSlug, courseSlug),
    queryFn: () => api.courses.getBySlug(token, orgSlug, courseSlug),
    enabled: token.length > 0 && orgSlug.length > 0 && courseSlug.length > 0,
    staleTime: 30_000,
  });
}

export function useModulesQuery(token: string, courseId: string | undefined) {
  return useQuery({
    queryKey: modulesKey(courseId ?? 'none'),
    queryFn: () => api.modules.listForCourse(token, courseId!),
    enabled: token.length > 0 && Boolean(courseId),
    staleTime: 15_000,
  });
}

export function useCreateModuleMutation(token: string, courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { parentModuleId?: string | null; title: string; description?: string }) =>
      api.modules.create(token, courseId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: modulesKey(courseId) });
    },
  });
}

export function useItemsQuery(token: string, moduleId: string | undefined) {
  return useQuery({
    queryKey: itemsKey(moduleId ?? 'none'),
    queryFn: () => api.moduleItems.listForModule(token, moduleId!),
    enabled: token.length > 0 && Boolean(moduleId),
    staleTime: 15_000,
  });
}

export function useCreateItemMutation(token: string, moduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { type: ModuleItemType; title: string; payloadJson?: string }) =>
      api.moduleItems.create(token, moduleId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: itemsKey(moduleId) });
    },
  });
}

export function useBlocksQuery(token: string, itemId: string | undefined) {
  return useQuery({
    queryKey: blocksKey(itemId ?? 'none'),
    queryFn: () => api.blocks.listForItem(token, itemId!),
    enabled: token.length > 0 && Boolean(itemId),
    staleTime: 5_000,
  });
}

export function useCreateBlockMutation(token: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { type: ContentBlockType; payloadJson: string }) =>
      api.blocks.create(token, itemId, body),
    onSuccess: ({ block }) => {
      qc.setQueryData<{ blocks: ContentBlockShape[] }>(blocksKey(itemId), (prev) =>
        prev ? { blocks: [...prev.blocks, block] } : { blocks: [block] },
      );
    },
  });
}

export function useUpdateBlockMutation(token: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ blockId, payloadJson }: { blockId: string; payloadJson: string }) =>
      api.blocks.update(token, blockId, { payloadJson }),
    onSuccess: ({ block }) => {
      qc.setQueryData<{ blocks: ContentBlockShape[] }>(blocksKey(itemId), (prev) => {
        if (!prev) return { blocks: [block] };
        return { blocks: prev.blocks.map((b) => (b.id === block.id ? block : b)) };
      });
    },
  });
}

export function useDeleteBlockMutation(token: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blockId: string) => api.blocks.remove(token, blockId),
    onMutate: (blockId) => {
      qc.setQueryData<{ blocks: ContentBlockShape[] }>(blocksKey(itemId), (prev) =>
        prev ? { blocks: prev.blocks.filter((b) => b.id !== blockId) } : prev,
      );
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: blocksKey(itemId) });
    },
  });
}
